import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { notifyAPI } from '../lib/api';
import useSocket from '../hooks/useSocket';
import { Avatar, fmtDateTime } from './ui';

const TYPE_TEXT = {
  assigned: 'assigned you',
  comment: 'commented on',
  mention: 'mentioned you in',
  completed: 'completed',
  due: 'due today:',
  share_assigned: 'asked you to share',
  share_due: 'time to share:',
};

export default function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const refreshBadge = useCallback(() => {
    notifyAPI.badge().then(({ data }) => setUnread(data.unread));
  }, []);

  useEffect(() => {
    refreshBadge();
  }, [refreshBadge]);

  const onEvent = useCallback(
    (ev) => {
      if (ev.type === 'notify') refreshBadge();
    },
    [refreshBadge]
  );
  useSocket(onEvent);

  useEffect(() => {
    if (!open) return undefined;
    notifyAPI.list().then(({ data }) => setItems(data.notifications));
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markAll = async () => {
    await notifyAPI.markRead(null);
    setItems((prev) => prev.map((i) => ({ ...i, read: true })));
    setUnread(0);
  };

  const openItem = async (n) => {
    if (!n.read) {
      await notifyAPI.markRead([n.id]);
      refreshBadge();
    }
    setOpen(false);
    // Route tasks through My Work's standalone panel: it resolves via task-level
    // access, so an assigned operator/contributor who isn't a project member can
    // still open it (the project board would 404 for them).
    if (n.subject?.type === 'task') navigate(`/my-work?task=${n.subject.id}`);
    else if (n.project_id) navigate(`/projects/${n.project_id}/discussion`);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title="Notifications"
        className="relative rounded-md p-2 text-pm-muted hover:bg-pm-raised hover:text-pm-text"
      >
        <Bell size={16} aria-hidden />
        {unread > 0 && (
          <span className="pm-num absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-pm-accent px-1 text-[9px] font-bold text-pm-accent-ink">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
        <span className="sr-only">Notifications</span>
      </button>
      {open && (
        <div className="pm-modal-in absolute right-0 top-10 z-50 w-80 rounded-lg border border-pm-border bg-pm-surface shadow-[var(--pm-elev-3)]">
          <div className="flex items-center justify-between border-b border-pm-border px-3 py-2">
            <span className="text-xs font-semibold">Notifications</span>
            <button type="button" onClick={markAll} className="inline-flex items-center gap-1 text-[11px] text-pm-muted hover:text-pm-text">
              <CheckCheck size={12} /> Mark all read
            </button>
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => openItem(n)}
                  className={`flex w-full items-start gap-2 px-3 py-2.5 text-left hover:bg-pm-raised/60 ${n.read ? 'opacity-60' : ''}`}
                >
                  <Avatar name={n.actor_name || 'S'} size={22} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs">
                      <b>{n.actor_name || 'Scheduler'}</b> {TYPE_TEXT[n.type] || n.type}{' '}
                      <span className="text-pm-muted">{n.subject?.title}</span>
                    </span>
                    <span className="text-[10px] text-pm-faint">{fmtDateTime(n.created_at)}</span>
                  </span>
                  {!n.read && <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-pm-accent" />}
                </button>
              </li>
            ))}
            {items.length === 0 && <li className="px-3 py-8 text-center text-xs text-pm-faint">Nothing yet.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
